import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { capabilityAssessmentsService } from '../../../services/enablementService';
import { partnerService } from '../../../services/partnerService';
import { useToast } from '../../../contexts/ToastContext';

const DIMENSIONS = [
  { key: 'technical_score', label: 'Technical', icon: 'Wrench', description: 'Engineering expertise and technical infrastructure' },
  { key: 'sales_score', label: 'Sales', icon: 'TrendingUp', description: 'Sales team structure and deal management' },
  { key: 'training_score', label: 'Training', icon: 'GraduationCap', description: 'Training completion and certification coverage' },
  { key: 'delivery_score', label: 'Delivery', icon: 'Package', description: 'Project delivery capability and service quality' },
  { key: 'support_score', label: 'Support', icon: 'Headphones', description: 'Customer support infrastructure and SLA compliance' },
];

const BENCHMARK = { technical_score: 80, sales_score: 75, training_score: 70, delivery_score: 75, support_score: 80 };

const RadarChart = ({ scores }) => {
  const n = DIMENSIONS?.length;
  const cx = 180, cy = 180, r = 130;
  const angleStep = (2 * Math.PI) / n;
  const getPoint = (score, i) => {
    const angle = angleStep * i - Math.PI / 2;
    const d = (score / 100) * r;
    return { x: cx + d * Math.cos(angle), y: cy + d * Math.sin(angle) };
  };
  const getLabelPoint = (i) => {
    const angle = angleStep * i - Math.PI / 2;
    const d = r + 36;
    return { x: cx + d * Math.cos(angle), y: cy + d * Math.sin(angle) };
  };
  const getBenchmarkPoint = (i) => {
    const angle = angleStep * i - Math.PI / 2;
    const d = (BENCHMARK?.[DIMENSIONS?.[i]?.key] / 100) * r;
    return { x: cx + d * Math.cos(angle), y: cy + d * Math.sin(angle) };
  };
  const dataPoints = DIMENSIONS?.map((dim, i) => getPoint(scores?.[dim?.key] || 0, i));
  const benchmarkPoints = DIMENSIONS?.map((_, i) => getBenchmarkPoint(i));
  const dataPath = dataPoints?.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p?.x} ${p?.y}`)?.join(' ') + ' Z';
  const benchmarkPath = benchmarkPoints?.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p?.x} ${p?.y}`)?.join(' ') + ' Z';
  const gridLevels = [20, 40, 60, 80, 100];

  return (
    <svg viewBox="0 0 360 360" className="w-full h-auto max-w-sm mx-auto">
      {gridLevels?.map(level => {
        const pts = DIMENSIONS?.map((_, i) => { const angle = angleStep * i - Math.PI / 2; const d = (level / 100) * r; return { x: cx + d * Math.cos(angle), y: cy + d * Math.sin(angle) }; });
        const path = pts?.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p?.x} ${p?.y}`)?.join(' ') + ' Z';
        return <path key={level} d={path} fill="none" stroke="#e2e8f0" strokeWidth="1" />;
      })}
      {DIMENSIONS?.map((_, i) => { const ep = getPoint(100, i); return <line key={i} x1={cx} y1={cy} x2={ep?.x} y2={ep?.y} stroke="#e2e8f0" strokeWidth="1" />; })}
      <path d={benchmarkPath} fill="none" stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="4 3" opacity={0.7} />
      <path d={dataPath} fill="#6366f1" fillOpacity={0.2} stroke="#6366f1" strokeWidth="2.5" />
      {dataPoints?.map((p, i) => <circle key={i} cx={p?.x} cy={p?.y} r="5" fill="#6366f1" />)}
      {DIMENSIONS?.map((dim, i) => {
        const lp = getLabelPoint(i);
        return (
          <text key={i} x={lp?.x} y={lp?.y} textAnchor="middle" dominantBaseline="middle" fontSize="11" fontWeight="600" fill="#475569">{dim?.label}</text>
        );
      })}
      {gridLevels?.filter(l => l % 40 === 0)?.map(level => (
        <text key={level} x={cx + 6} y={cy - (level / 100) * r + 4} fontSize="9" fill="#94a3b8">{level}</text>
      ))}
    </svg>
  );
};

const SaveAssessmentModal = ({ partnerId, existing, onClose, onSuccess }) => {
  const [form, setForm] = useState(existing ? {
    technical_score: existing?.technical_score, sales_score: existing?.sales_score,
    training_score: existing?.training_score, delivery_score: existing?.delivery_score,
    support_score: existing?.support_score, gap_notes: existing?.gap_notes || '', recommendations: existing?.recommendations || ''
  } : { technical_score: 70, sales_score: 70, training_score: 70, delivery_score: 70, support_score: 70, gap_notes: '', recommendations: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const handleChange = (e) => setForm(p => ({ ...p, [e?.target?.name]: e?.target?.type === 'range' ? parseInt(e?.target?.value) : e?.target?.value }));
  const overall = Math.round(DIMENSIONS?.reduce((sum, d) => sum + (form?.[d?.key] || 0), 0) / DIMENSIONS?.length);
  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true); setError(null);
    try {
      if (existing?.id) { await capabilityAssessmentsService?.update(existing?.id, { ...form, overall_score: overall }); }
      else { await capabilityAssessmentsService?.create({ partner_id: partnerId, ...form, overall_score: overall, assessment_date: new Date()?.toISOString()?.split('T')?.[0] }); }
      onSuccess();
    } catch (err) { setError(err?.message || 'Failed to save assessment.'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-24 py-18 border-b border-slate-100 bg-gradient-to-r from-indigo-50 to-violet-50 flex-shrink-0">
          <div className="flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center"><Icon name="BarChart3" size={18} className="text-white" /></div>
            <div><h2 className="text-base font-bold text-slate-900">{existing ? 'Update' : 'New'} Assessment</h2><p className="text-xs text-slate-500 mt-1">Overall: {overall}/100</p></div>
          </div>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500"><Icon name="X" size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-24 space-y-16 overflow-y-auto flex-1">
          {error && <div className="flex items-center gap-10 p-12 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><Icon name="AlertCircle" size={15} />{error}</div>}
          {DIMENSIONS?.map(dim => (
            <div key={dim?.key}>
              <div className="flex items-center justify-between mb-8">
                <label className="flex items-center gap-8 text-sm font-semibold text-slate-700"><Icon name={dim?.icon} size={15} className="text-indigo-500" />{dim?.label}</label>
                <span className={`text-sm font-bold ${form?.[dim?.key] >= 80 ? 'text-emerald-600' : form?.[dim?.key] >= 60 ? 'text-amber-600' : 'text-red-600'}`}>{form?.[dim?.key]}/100</span>
              </div>
              <input type="range" name={dim?.key} min="0" max="100" value={form?.[dim?.key]} onChange={handleChange} className="w-full h-2 rounded-full appearance-none bg-slate-200 accent-indigo-600" />
              <div className="flex justify-between text-xs text-slate-400 mt-4"><span>0</span><span>Benchmark: {BENCHMARK?.[dim?.key]}</span><span>100</span></div>
            </div>
          ))}
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Gap Notes</label><textarea name="gap_notes" value={form?.gap_notes} onChange={handleChange} rows={2} placeholder="Identified gaps and areas needing improvement..." className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 resize-none" /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Recommendations</label><textarea name="recommendations" value={form?.recommendations} onChange={handleChange} rows={2} placeholder="Action items and recommendations..." className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 resize-none" /></div>
          <div className="flex items-center gap-12 pt-8 border-t border-slate-100">
            <button type="button" onClick={onClose} className="flex-1 px-20 py-11 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-20 py-11 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-8">
              {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving...</> : <><Icon name="Save" size={16} />Save Assessment</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CapabilityAssessment = () => {
  const toast = useToast();
  const [partners, setPartners] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => { loadPartners(); }, []);
  useEffect(() => { if (selectedPartnerId) loadAssessment(); else setAssessment(null); }, [selectedPartnerId]);

  const loadPartners = async () => {
    try { const pts = await partnerService?.getAll(); setPartners(pts || []); if (pts?.length > 0) setSelectedPartnerId(pts?.[0]?.id); }
    catch (err) { toast?.error('Failed to load partners.'); }
  };

  const loadAssessment = async () => {
    setLoading(true);
    try { const a = await capabilityAssessmentsService?.getLatestByPartner(selectedPartnerId); setAssessment(a); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const scores = assessment || { technical_score: 0, sales_score: 0, training_score: 0, delivery_score: 0, support_score: 0, overall_score: 0 };
  const selectedPartner = partners?.find(p => p?.id === selectedPartnerId);

  const gapDimensions = DIMENSIONS?.filter(d => (scores?.[d?.key] || 0) < BENCHMARK?.[d?.key]);

  return (
    <div className="space-y-20">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-24">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-16">
          <div className="flex items-center gap-14">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/25">
              <Icon name="BarChart3" size={22} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Capability Assessment</h2>
              <p className="text-sm text-slate-500 mt-1">Radar chart analysis with benchmark gap identification</p>
            </div>
          </div>
          <div className="flex items-center gap-12">
            <select value={selectedPartnerId} onChange={e => setSelectedPartnerId(e?.target?.value)} className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 bg-white min-w-[200px]">
              <option value="">— Select Partner —</option>
              {partners?.map(p => <option key={p?.id} value={p?.id}>{p?.brand_name || p?.legal_name}</option>)}
            </select>
            {selectedPartnerId && (
              <button onClick={() => setShowModal(true)} className="flex items-center gap-8 px-20 py-11 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all">
                <Icon name={assessment ? 'RefreshCw' : 'Plus'} size={16} />{assessment ? 'Update' : 'New Assessment'}
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-48"><div className="w-10 h-10 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" /></div>
      ) : !selectedPartnerId ? (
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-48 text-center"><Icon name="BarChart3" size={40} className="text-slate-300 mx-auto mb-16" /><p className="text-slate-500 font-medium">Select a partner to view capability assessment</p></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
          {/* Radar Chart */}
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-24">
            <div className="flex items-center justify-between mb-20">
              <h3 className="text-base font-bold text-slate-900">Capability Radar</h3>
              <div className="flex items-center gap-12 text-xs">
                <span className="flex items-center gap-6"><span className="w-3 h-3 rounded-full bg-indigo-500 inline-block" />Current</span>
                <span className="flex items-center gap-6"><span className="w-3 h-0.5 bg-amber-500 inline-block border-dashed" />Benchmark</span>
              </div>
            </div>
            {!assessment ? (
              <div className="text-center py-32"><Icon name="BarChart3" size={36} className="text-slate-300 mx-auto mb-12" /><p className="text-slate-400 text-sm">No assessment data yet</p><button onClick={() => setShowModal(true)} className="mt-12 px-16 py-8 rounded-xl bg-indigo-50 text-indigo-700 text-sm font-semibold hover:bg-indigo-100 transition-colors">Create First Assessment</button></div>
            ) : (
              <>
                <RadarChart scores={scores} />
                <div className="mt-16 text-center">
                  <div className="text-3xl font-bold text-indigo-600">{scores?.overall_score}</div>
                  <div className="text-xs text-slate-500 mt-4">Overall Score / 100</div>
                  {assessment?.assessment_date && <div className="text-xs text-slate-400 mt-4">Assessed: {new Date(assessment?.assessment_date)?.toLocaleDateString()}</div>}
                </div>
              </>
            )}
          </div>

          {/* Dimension Scores + Gap Analysis */}
          <div className="space-y-16">
            <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-24">
              <h3 className="text-base font-bold text-slate-900 mb-16">Dimension Scores</h3>
              <div className="space-y-14">
                {DIMENSIONS?.map(dim => {
                  const score = scores?.[dim?.key] || 0;
                  const benchmark = BENCHMARK?.[dim?.key];
                  const gap = score - benchmark;
                  return (
                    <div key={dim?.key} className="space-y-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-8"><Icon name={dim?.icon} size={15} className="text-indigo-500" /><span className="text-sm font-semibold text-slate-700">{dim?.label}</span></div>
                        <div className="flex items-center gap-10">
                          <span className={`text-xs font-semibold px-8 py-3 rounded-lg ${gap >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{gap >= 0 ? '+' : ''}{gap}</span>
                          <span className="text-sm font-bold text-slate-900">{score}/100</span>
                        </div>
                      </div>
                      <div className="relative h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${score}%`, background: score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444' }} />
                        <div className="absolute top-0 h-full w-0.5 bg-amber-500" style={{ left: `${benchmark}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Gap Analysis */}
            {gapDimensions?.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-24">
                <div className="flex items-center gap-10 mb-16">
                  <Icon name="AlertTriangle" size={18} className="text-amber-500" />
                  <h3 className="text-base font-bold text-slate-900">Gap Analysis</h3>
                </div>
                <div className="space-y-12">
                  {gapDimensions?.map(dim => {
                    const score = scores?.[dim?.key] || 0;
                    const gap = BENCHMARK?.[dim?.key] - score;
                    return (
                      <div key={dim?.key} className="flex items-start gap-12 p-14 bg-amber-50 border border-amber-200 rounded-xl">
                        <Icon name={dim?.icon} size={16} className="text-amber-600 flex-shrink-0 mt-1" />
                        <div>
                          <p className="text-sm font-semibold text-amber-800">{dim?.label} — {gap} points below benchmark</p>
                          <p className="text-xs text-amber-700 mt-2">{dim?.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {assessment?.recommendations && (
                  <div className="mt-16 p-14 bg-indigo-50 border border-indigo-200 rounded-xl">
                    <p className="text-xs font-semibold text-indigo-700 mb-6">Recommendations</p>
                    <p className="text-xs text-indigo-600 leading-relaxed">{assessment?.recommendations}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {showModal && selectedPartnerId && (
        <SaveAssessmentModal
          partnerId={selectedPartnerId}
          existing={assessment}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); loadAssessment(); toast?.success('Assessment saved.'); }}
        />
      )}
    </div>
  );
};

export default CapabilityAssessment;
